import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { createPortal } from 'react-dom';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import PropTypes from 'prop-types';
import { API } from '../../config';
import Buttons from '../Button';
import '../DnDTable/DnDTable.scss';
import './joblinkMap.scss';

const FloatingMap = ({
  isVisible,
  onClose,
  currentLocation,
  geocodedJobs,
  selectedPlace,
  showingInfoWindow,
  onMarkerClick,
  onInfoWindowClose,
  getMarkerColor,
  markerRefs,
  startGlobalDrag,
  mapTaskDndSource,
  handleClickJobListItem,
  toggleCoachmarksVisibility,
  currentCoachmarkTarget,
  onMapReady,
  sharedMapCenter,
  sharedMapZoom,
}) => {
  const mapRef = useRef(null);
  const [isDraggingPin, setIsDraggingPin] = useState(false);
  const [draggedJobId, setDraggedJobId] = useState(null);
  const dragPreviewRef = useRef(null);
  const draggedJobRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Determine initial center - use shared position if available, otherwise current location
  const initialCenter = sharedMapCenter || currentLocation;
  const initialZoom = sharedMapZoom || 14;

  const handleResize = () => {
    if (mapRef.current) {
      setTimeout(() => {
        globalThis.google?.maps?.event?.trigger(mapRef.current, 'resize');
      }, 100);
    }
  };

  const createDragPreview = useCallback((job) => {
    const preview = document.createElement('div');
    preview.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 100000;
      background: ${getMarkerColor(job.type)};
      min-width: 80px;
      height: 30px;
      border-radius: 4px;
      color: white;
      padding: 5px;
      font-size: 12px;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    preview.textContent = job.customerName;
    return preview;
  }, [getMarkerColor]);

  const findDropTarget = useCallback((x, y) => {
    const elements = document.elementsFromPoint(x, y);
    return elements.find(
      (el) =>
        el.classList.contains('timeline-slot') ||
        el.classList.contains('resource-view') ||
        el.classList.contains('scheduler-content') ||
        el.classList.contains('rbs-content-cell')
    );
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragPreviewRef.current) return;
    dragPreviewRef.current.style.left = e.clientX + 'px';
    dragPreviewRef.current.style.top = e.clientY + 'px';

    // Highlight drop target
    const elements = document.querySelectorAll('.drag-over');
    for (const el of elements) {
      el.classList.remove('drag-over');
    }

    const dropTarget = findDropTarget(e.clientX, e.clientY);
    if (dropTarget) {
      dropTarget.classList.add('drag-over');
    }
  }, [findDropTarget]);

  const handleMouseUp = useCallback((e) => {
    const dropTarget = findDropTarget(e.clientX, e.clientY);
    const job = draggedJobRef.current;

    // Clean up drag preview
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
    }
    dragPreviewRef.current = null;

    // Clean up drag-over highlights
    const elements = document.querySelectorAll('.drag-over');

    for (const el of elements) {
      el.classList.remove('drag-over');
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Reset state
    setIsDraggingPin(false);
    setDraggedJobId(null);

    // If dropped on valid target, call the original startGlobalDrag logic
    // which handles the scheduler integration
    if (dropTarget && job) {
      // The job was dropped on the scheduler - it will be removed by the parent's handleDrop
      startGlobalDrag(job, e);
      // Simulate immediate mouse up to trigger the drop
      setTimeout(() => {
        const mouseUpEvent = new MouseEvent('mouseup', {
          clientX: e.clientX,
          clientY: e.clientY,
          bubbles: true,
        });
        document.dispatchEvent(mouseUpEvent);
      }, 50);
    }
    // If not dropped on valid target, pin stays on map (no action needed)

    draggedJobRef.current = null;
  }, [findDropTarget, handleMouseMove, startGlobalDrag]);

  const handlePinDragStart = useCallback((job, e) => {
    // Prevent Google Maps default drag behavior
    if (e?.domEvent) {
      e.domEvent.stopPropagation();
      e.domEvent.preventDefault();
    }

    // Get initial position before setting state
    const clientX = e?.domEvent?.clientX || e?.clientX || window.innerWidth / 2;
    const clientY = e?.domEvent?.clientY || e?.clientY || window.innerHeight / 2;

    // Create and position drag preview BEFORE changing state
    const preview = createDragPreview(job);
    preview.style.left = clientX + 'px';
    preview.style.top = clientY + 'px';
    document.body.appendChild(preview);
    dragPreviewRef.current = preview;
    draggedJobRef.current = job;

    // Add global event listeners BEFORE changing state
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Now set state to trigger transparency
    setIsDraggingPin(true);
    setDraggedJobId(job.id);
  }, [createDragPreview, handleMouseMove, handleMouseUp]);

  // Reset initialization flag when visibility changes
  useEffect(() => {
    if (!isVisible) {
      hasInitializedRef.current = false;
    }
  }, [isVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dragPreviewRef.current) {
        dragPreviewRef.current.remove();
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  if (!isVisible) return null;

  return createPortal(
    <Rnd
      default={{
        x: (window.innerWidth - window.innerHeight * 0.7) / 2,
        y: (window.innerHeight - window.innerHeight * 0.7) / 2,
        width: window.innerHeight * 0.7,
        height: window.innerHeight * 0.7,
      }}
      minWidth={400}
      minHeight={300}
      bounds="window"
      dragHandleClassName="drag-handle"
      onResize={handleResize}
      disableDragging={isDraggingPin}
      style={{
        zIndex: isDraggingPin ? 1 : 9999,
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: isDraggingPin ? 'none' : '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        opacity: isDraggingPin ? 0.3 : 1,
        pointerEvents: isDraggingPin ? 'none' : 'auto',
        transition: 'opacity 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <div className="w-full h-full flex flex-col">
        <div className="drag-handle flex justify-between items-center p-2 bg-gray-100 cursor-move rounded-t-xl">
          <h1 className="hh font-semibold text-gray-700">
            <FormattedMessage id="JobMaps" defaultMessage="JobMaps" />
          </h1>
          <Buttons color="#b71b1b" text="Close" onBtnClick={onClose} />
        </div>

        <div className="flex-1 p-2">
          <APIProvider apiKey={API.MAP_KEY}>
            <Map
              id="job-list-map"
              ref={mapRef}
              defaultZoom={initialZoom}
              defaultCenter={initialCenter}
              style={{ width: '100%', height: '100%', borderRadius: '8px' }}
              gestureHandling={'greedy'}
              disableDefaultUI={false}
              fullscreenControl={false}
              mapId={API.MAP_KEY}
              onTilesLoaded={(ev) => {
                if (ev?.map && onMapReady && !hasInitializedRef.current) {
                  hasInitializedRef.current = true;
                  onMapReady(ev.map);
                }
              }}
              onClick={() => {
                if (showingInfoWindow) {
                  onInfoWindowClose();
                }
              }}
            >
              <AdvancedMarker position={currentLocation}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#4285F4',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                />
              </AdvancedMarker>

              {geocodedJobs.map((job, index) => {
                const MapDnDTaskItem = mapTaskDndSource.getDragSource();
                return (
                  <AdvancedMarker
                    key={`${job.id}-${index}`}
                    position={job.geocodedCoords}
                    draggable={!isDraggingPin}
                    onClick={() => !isDraggingPin && onMarkerClick(job)}
                    onDragStart={(e) => {
                      handlePinDragStart(job, e);
                    }}
                  >
                    <div
                      ref={(el) => (markerRefs[job.id] = el)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: getMarkerColor(job.type),
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        cursor: isDraggingPin ? 'default' : 'grab',
                        position: 'relative',
                        zIndex: 1000,
                        opacity: draggedJobId === job.id ? 0.5 : 1,
                      }}
                      title={`${job.customerName} - ${job.type}`}
                    />
                    {MapDnDTaskItem && (
                      <div style={{ display: 'none' }}>
                        <MapDnDTaskItem
                          task={job}
                          handleClickJobListItem={handleClickJobListItem}
                          toggleCoachmarksVisibility={
                            toggleCoachmarksVisibility
                          }
                          currentCoachmarkTarget={currentCoachmarkTarget}
                        />
                      </div>
                    )}
                  </AdvancedMarker>
                );
              })}

              {showingInfoWindow && selectedPlace && (
                <InfoWindow
                  position={selectedPlace.geocodedCoords}
                  onCloseClick={onInfoWindowClose}
                >
                  <div
                    className="job-details-hover-text"
                    style={{
                      backgroundColor: '#333',
                      color: '#fff',
                      padding: '10px',
                      borderRadius: '5px',
                      minWidth: '200px',
                      position: 'relative',
                    }}
                  >
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      <li style={{ marginBottom: '5px' }}>
                        Name:- {selectedPlace?.customerName}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Id:- {selectedPlace?.jobId}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Category:- {selectedPlace?.reportCategory}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Type:- {selectedPlace?.type}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Frequency:- {selectedPlace?.frequency}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Description:- {selectedPlace?.description}
                      </li>
                    </ul>
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        </div>
      </div>
    </Rnd>,
    document.body
  );
};

FloatingMap.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  currentLocation: PropTypes.object,
  geocodedJobs: PropTypes.array,
  selectedPlace: PropTypes.object,
  showingInfoWindow: PropTypes.bool,
  onMarkerClick: PropTypes.func,
  onInfoWindowClose: PropTypes.func,
  getMarkerColor: PropTypes.func,
  markerRefs: PropTypes.object,
  startGlobalDrag: PropTypes.func,
  mapTaskDndSource: PropTypes.object,
  handleClickJobListItem: PropTypes.func,
  toggleCoachmarksVisibility: PropTypes.func,
  currentCoachmarkTarget: PropTypes.string,
  onMapReady: PropTypes.func,
  sharedMapCenter: PropTypes.object,
  sharedMapZoom: PropTypes.number,
};

export default FloatingMap;
